import { create } from "zustand";
import type { StateStorage } from "zustand/middleware";
import { createJSONStorage, persist } from "zustand/middleware";
import type { CalendarLocale } from "@/shared/lib/date/calendar-locale";
import { ApplicationError } from "@/shared/lib/errors";
import { i18n } from "@/shared/lib/i18n";
import type { Locale } from "@/shared/lib/i18n/types";
import type { ViewerSplitPanePercent } from "@/shared/lib/percent";
import {
	StorageKeys,
	StorageSchemas,
	typedLocalStorage,
} from "@/shared/lib/storage/typed-storage";
import { localStorageAdapter } from "@/shared/lib/storage-utils";
import {
	DEFAULT_PERSISTED_SETTINGS,
	isWritableSettingsStorageValue,
	normalizeStoredSettingsValue,
} from "./settings-storage";

// ============================================================================
// Settings Store
// ============================================================================

// Must match UI constraints (Documents split pane uses minTopHeight="10%" and minBottomHeight="10%")
const TOOLBAR_SIZE_PERCENTAGE_SCALE = 100;
const DEFAULT_TOOLBAR_SIZE_PERCENT = 100;
export const CALENDAR_DENSITY_PERCENTAGE_SCALE = 100;

export type ComponentStyle = "t0ggle" | "default";
export type ViewerSplitOrientation = "vertical" | "horizontal";

export interface SettingsState {
	_hasHydrated: boolean;
	calendarDensity: number;
	calendarLocale: CalendarLocale;
	componentStyle: ComponentStyle;
	editorToolbarSize: number;
	freeRoam: boolean;
	sendTypingIndicator: boolean;
	setCalendarDensity: (density: number) => void;
	setCalendarLocale: (locale: CalendarLocale) => void;
	setComponentStyle: (style: ComponentStyle) => void;
	setEditorToolbarSize: (size: number) => void;
	setFreeRoam: (freeRoam: boolean) => void;
	setSendTypingIndicator: (send: boolean) => void;
	setShowDualCalendar: (show: boolean) => void;
	setShowToolCalls: (show: boolean) => void;

	// Actions
	setTheme: (theme: string) => void;
	setViewerEnabled: (enabled: boolean) => void;
	setViewerSplitOrientation: (orientation: ViewerSplitOrientation) => void;
	setViewerSplitPaneHeight: (height: ViewerSplitPanePercent | null) => void;
	setViewerSplitPaneLocked: (locked: boolean) => void;
	setViewerToolbarHidden: (hidden: boolean) => void;
	showDualCalendar: boolean;
	showToolCalls: boolean;
	// State
	theme: string;
	viewerEnabled: boolean;
	/** "vertical" stacks the viewer above the editor; "horizontal" sets them side by side. */
	viewerSplitOrientation: ViewerSplitOrientation;
	viewerSplitPaneHeight: ViewerSplitPanePercent | null;
	viewerSplitPaneLocked: boolean;
	viewerToolbarHidden: boolean;
}

/**
 * Validated storage adapter for settings store.
 * Uses localStorageAdapter for caching to avoid expensive synchronous
 * localStorage reads on every access.
 */
const validatedSettingsStateStorage: StateStorage = {
	getItem: (key) => {
		// Use cached adapter - avoids repeated synchronous localStorage.getItem calls
		const raw = localStorageAdapter.getItem(key);
		if (raw === null) {
			return null;
		}
		if (key !== StorageKeys.settingsStore) {
			return raw;
		}
		return normalizeStoredSettingsValue(raw);
	},
	setItem: (key, value) => {
		if (typeof window === "undefined") {
			return;
		}
		try {
			if (key === StorageKeys.settingsStore) {
				const parsed = JSON.parse(value) as unknown;
				if (!isWritableSettingsStorageValue(parsed)) {
					// In test environment, skip validation instead of throwing
					if (import.meta.env.MODE === "test") {
						localStorageAdapter.setItem(key, value);
						return;
					}
					throw ApplicationError.badRequest(
						"settings-store: invalid persisted state",
						"INVALID_PERSISTED_STATE",
						{ reason: "schema-validation" },
					);
				}
			}
			// Use cached adapter - updates cache on write
			localStorageAdapter.setItem(key, value);
		} catch (error) {
			// In test environment, suppress validation errors
			if (import.meta.env.MODE === "test") {
				try {
					localStorageAdapter.setItem(key, value);
				} catch {
					// Storage unavailable in test environment - this is expected
				}
				return;
			}
			// Re-throw if not a test environment, unless it's a storage error
			if (error instanceof Error && error.message.includes("settings-store")) {
				throw error;
			}
			// Silently fail for storage errors (quota exceeded, etc.)
		}
	},
	removeItem: (key) => {
		// Use cached adapter - removes from cache as well
		localStorageAdapter.removeItem(key);
	},
};

// Data-only fields of SettingsState (everything except the `set*` action
// functions). This lets us precisely type `initializeSettings()` as the
// portion of state it actually produces, without an unsafe cast at the call
// site.
type SettingsStateData = Omit<
	SettingsState,
	| "setTheme"
	| "setFreeRoam"
	| "setShowDualCalendar"
	| "setShowToolCalls"
	| "setSendTypingIndicator"
	| "setViewerEnabled"
	| "setViewerSplitPaneLocked"
	| "setViewerSplitPaneHeight"
	| "setViewerSplitOrientation"
	| "setViewerToolbarHidden"
	| "setEditorToolbarSize"
	| "setCalendarDensity"
	| "setCalendarLocale"
	| "setComponentStyle"
>;

// Initialize settings
function initializeSettings(): SettingsStateData {
	return {
		...DEFAULT_PERSISTED_SETTINGS,
		/*
		 * FLAT BY DEFAULT.
		 *
		 * `DEFAULT_PERSISTED_SETTINGS` says `"t0ggle"`, so `useIsEmbossed()`
		 * returned true unless a surface explicitly opted out — which the navbar,
		 * the composer card, the chat selector and the calendar drawer each had to
		 * do with `EmbossedOverrideContext`, every one of them after someone
		 * restyled a flat branch that was never the one rendering.
		 *
		 * The control that changed this lived in the account page's appearance
		 * section, which is gone; leaving the default embossed would have stranded
		 * everyone on a look with no way out of it. The `"t0ggle"` value and the
		 * branches that read it are left in place — the embossed theme still works
		 * if it is ever reinstated — but nothing selects it now.
		 */
		componentStyle: "default",
		_hasHydrated: false,
	};
}

export const useSettingsStore = create<SettingsState>()(
	persist<
		SettingsState,
		[],
		[],
		Omit<
			SettingsState,
			| "setTheme"
			| "setFreeRoam"
			| "setShowDualCalendar"
			| "setShowToolCalls"
			| "setSendTypingIndicator"
			| "setViewerEnabled"
			| "setViewerSplitPaneLocked"
			| "setViewerSplitPaneHeight"
			| "setViewerSplitOrientation"
			| "setViewerToolbarHidden"
			| "setEditorToolbarSize"
			| "setCalendarDensity"
			| "setCalendarLocale"
			| "setComponentStyle"
			| "_hasHydrated"
		>
	>(
		(set) => {
			const initialState = initializeSettings();

			// Initialize CSS variables for toolbar scale
			if (typeof window !== "undefined") {
				const toolbarSize =
					initialState.editorToolbarSize ?? DEFAULT_TOOLBAR_SIZE_PERCENT;
				document.documentElement.style.setProperty(
					"--tldraw-toolbar-scale",
					String(toolbarSize / TOOLBAR_SIZE_PERCENTAGE_SCALE),
				);
			}

			return {
				// Initial state
				...initialState,

				// Actions
				setTheme: (theme) => {
					set({ theme });
					// Persist theme separately for components that need it outside of React
					typedLocalStorage.set(StorageKeys.theme, theme, StorageSchemas.theme);
				},
				setFreeRoam: (freeRoam) => {
					set({ freeRoam });
				},
				setShowDualCalendar: (show) => {
					set({ showDualCalendar: show });
				},
				setShowToolCalls: (show) => {
					set({ showToolCalls: show });
				},
				setSendTypingIndicator: (send) => {
					set({ sendTypingIndicator: send });
				},
				setViewerEnabled: (enabled) => {
					set({ viewerEnabled: enabled });
				},
				setViewerSplitPaneLocked: (locked) => {
					set({ viewerSplitPaneLocked: locked });
				},
				setViewerSplitPaneHeight: (height) => {
					set({ viewerSplitPaneHeight: height });
				},
				setViewerSplitOrientation: (orientation) => {
					set({ viewerSplitOrientation: orientation });
				},
				setViewerToolbarHidden: (hidden) => {
					set({ viewerToolbarHidden: hidden });
				},
				setEditorToolbarSize: (size) => {
					set({ editorToolbarSize: size });
					// Update CSS variable
					if (typeof window !== "undefined") {
						document.documentElement.style.setProperty(
							"--tldraw-toolbar-scale",
							String(size / TOOLBAR_SIZE_PERCENTAGE_SCALE),
						);
					}
				},
				setCalendarDensity: (density) => {
					set({ calendarDensity: density });
				},
				setCalendarLocale: (calendarLocale) => {
					set({ calendarLocale });
				},
				setComponentStyle: (componentStyle) => {
					set({ componentStyle });
				},
			};
		},
		{
			name: StorageKeys.settingsStore,
			version: 1,
			storage: createJSONStorage(() => validatedSettingsStateStorage),
			partialize: (state) => ({
				theme: state.theme,
				freeRoam: state.freeRoam,
				showDualCalendar: state.showDualCalendar,
				showToolCalls: state.showToolCalls,
				sendTypingIndicator: state.sendTypingIndicator,
				viewerEnabled: state.viewerEnabled,
				viewerSplitPaneLocked: state.viewerSplitPaneLocked,
				viewerSplitPaneHeight: state.viewerSplitPaneHeight,
				viewerSplitOrientation: state.viewerSplitOrientation,
				viewerToolbarHidden: state.viewerToolbarHidden,
				editorToolbarSize: state.editorToolbarSize,
				calendarDensity: state.calendarDensity,
				calendarLocale: state.calendarLocale,
				componentStyle: state.componentStyle,
			}),
			onRehydrateStorage: () => (state) => {
				// Use queueMicrotask to defer setState call until after the store is fully initialized.
				// This avoids the "Cannot access 'useSettingsStore' before initialization" TDZ error.
				queueMicrotask(() => {
					// Re-apply CSS variables from hydrated state, since store initialization
					// overwrites the inline script's values with defaults before hydration runs.
					if (
						typeof window !== "undefined" &&
						state &&
						state.editorToolbarSize != null
					) {
						document.documentElement.style.setProperty(
							"--tldraw-toolbar-scale",
							String(state.editorToolbarSize / TOOLBAR_SIZE_PERCENTAGE_SCALE),
						);
					}
					useSettingsStore.setState({ _hasHydrated: true });
				});
			},
		},
	),
);

// ============================================================================
// Language Store
// ============================================================================

export interface LanguageState {
	// State
	locale: Locale;

	// Actions
	setLocale: (locale: Locale) => void;
}

// Initialize language from persisted settings.
function initializeLanguage(): Locale {
	if (typeof window === "undefined") {
		return "en";
	}

	// Check for stored locale using typed storage
	const stored = typedLocalStorage.getItem(
		StorageKeys.locale,
		StorageSchemas.locale,
	);
	if (stored) {
		return stored;
	}

	return "en";
}

export const useLanguageStore = create<LanguageState>()((set) => {
	const initialLocale = initializeLanguage();

	// Initialize i18n and document lang/dir attributes on store creation
	if (typeof window !== "undefined") {
		try {
			i18n.changeLanguage(initialLocale);
			document.documentElement.setAttribute("lang", initialLocale);
			document.documentElement.setAttribute("dir", "ltr");
		} catch {
			// i18n not initialized yet, will be handled on first setLocale call
		}
	}

	return {
		locale: initialLocale,
		setLocale: (locale) => {
			set({ locale });
			if (typeof window !== "undefined") {
				try {
					i18n.changeLanguage(locale);
					document.documentElement.setAttribute("lang", locale);
					document.documentElement.setAttribute("dir", "ltr");
					typedLocalStorage.set(
						StorageKeys.locale,
						locale,
						StorageSchemas.locale,
					);
				} catch {
					// Locale storage errors are non-critical - ignore
				}
			}
		},
	};
});

// ============================================================================
// UI Store
// ============================================================================

export interface UIState {
	mobileSidebarOpen: boolean;
	selectedTab: string;
	setMobileSidebarOpen: (open: boolean) => void;
	setSelectedTab: (tab: string) => void;

	// Actions
	setSidebarOpen: (open: boolean) => void;
	// State
	sidebarOpen: boolean;
}

export const useUIStore = create<UIState>()((set) => ({
	// Initial state
	sidebarOpen: true,
	mobileSidebarOpen: false,
	selectedTab: "chat",

	// Actions
	setSidebarOpen: (open) => set({ sidebarOpen: open }),
	setMobileSidebarOpen: (open) => set({ mobileSidebarOpen: open }),
	setSelectedTab: (tab) => set({ selectedTab: tab }),
}));
